import { call, put, takeEvery, select, takeLatest } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';
import services from '../../services';
import * as models from '../../models'; 
import * as actions from './actions';
import * as claimActions from '../claims/actions';
import * as claimsConstants from '../claims/constants';
import * as constants from './constants';
import { ApiResponse } from 'services/api/base';
import { userActions } from 'store/user';
import { AppToastTypes } from 'models/enums'; 
import { USER_ACTIONS } from 'store/enums';
import { parseApiError } from 'utils/helpers';
import _ from 'lodash'; 


export default function* () {
    yield takeLatest(constants.REQUEST_AI_TOOL_PUBLISH, publishAITool);
    yield takeEvery(constants.REQUEST_CLAIM_LEVEL_AI_TOOLS, requestClaimLevelAiTools);
    yield takeLatest(constants.REQUEST_EXECUTE_AI_TOOL, requestExecuteAITool);
    yield takeEvery(constants.SET_AI_TOOL_EXECUTION_COMPLETE, handleExecutionComplete);
    yield takeEvery(constants.SUBMIT_AI_TOOL_INPUT, submitAIToolInput);
    yield takeLatest(constants.REQUEST_MATCH_RUNTIME_BUNDLE, requestMatchRuntimeBundle);
    yield takeLatest(constants.REQUEST_FETCH_CATALOG_TOOLS, requestFetchCatalogTools);
    yield takeEvery(constants.REQUEST_DELETE_AI_TOOL, requestDeleteAiTool);
    yield takeLatest(constants.REQUEST_AI_TOOL_CONFIGURATION, requestAiToolConfiguration);
    yield takeLatest(constants.UPDATE_AI_TOOL_CONFIGURATION, updateAiToolConfiguration);
    yield takeLatest(constants.CREATE_AI_TOOL_CONFIGURATION, createAiToolConfiguration);
}

export function* publishAITool(action: ActionType<typeof actions.requestAIToolPublish>) {
    const userRequest: ApiResponse = yield call(services.api.aiLab.publishAiToolVersion, action.payload.request);    
    if (userRequest.isSuccess) {
        yield put(actions.requestAIToolPublishSuccess());
        yield put(userActions.setLastUserAction(USER_ACTIONS.AI_TOOL_PUBLISHED));
        yield put(userActions.setAppToast({message: "AI tool published successfully", type: AppToastTypes.Success})); 
    }
    else {
        console.log(userRequest);  
        const errorMessage = parseApiError(userRequest);
        yield put(actions.requestAIToolPublishFailure(errorMessage));
        yield put(userActions.setLastUserAction(USER_ACTIONS.AI_TOOL_PUBLISH_FAILURE));
        yield put(userActions.setAppToast({message: `Failed to publish AI tool: ${errorMessage}`, type: AppToastTypes.Failure}));
    }        
}

export function* requestClaimLevelAiTools(action: ActionType<typeof actions.requestClaimLevelAiTools>) {
    const claimToolsRequest: ApiResponse = yield call(services.api.aiLab.getClaimLevelAiTools, action.payload.claimId);
    if (claimToolsRequest.isSuccess) {
        yield put(actions.receiveClaimLevelAiTools(claimToolsRequest.data));
    }
    else {
        console.log(claimToolsRequest);
        const errorMessage = parseApiError(claimToolsRequest);
        yield put(userActions.setAppToast({message: `Failed to load AI tools for claim: ${errorMessage}`, type: AppToastTypes.Failure}));
    }
}

export function* requestExecuteAITool(action: ActionType<typeof actions.requestExecuteAITool>) {
    const { claimId, toolId } = action.payload;
    const executeRequest: ApiResponse = yield call(services.api.aiLab.executeAITool, claimId, toolId);
    
    if (executeRequest.isSuccess) {
        yield put(actions.requestExecuteAIToolSuccess(executeRequest.data));
    } else {
        const errorMessage = parseApiError(executeRequest);
        yield put(actions.requestExecuteAIToolFailure(errorMessage));
        console.log(errorMessage); 
    }
}

export function* submitAIToolInput(action: ActionType<typeof actions.submitAIToolInput>) {
    const { claimId, toolId, inputPayload } = action.payload.input;
    
    const submitRequest: ApiResponse = yield call(
        services.api.aiLab.submitAIToolInput, 
        claimId, 
        toolId, 
        inputPayload
    );
    if (submitRequest.isSuccess) {
        yield put(actions.submitAIToolInputSuccess(submitRequest.data));
    } else {
        const errorMessage = parseApiError(submitRequest);
        yield put(actions.submitAIToolInputFailure(errorMessage));
        console.log(errorMessage); 
    }
}

export function* requestMatchRuntimeBundle(action: ActionType<typeof actions.requestMatchRuntimeBundle>) {
    const { toolId, files } = action.payload;
    const matchRequest: ApiResponse = yield call(services.api.aiLab.matchRuntimeBundle, toolId, files);

    if (matchRequest.isSuccess) {
        yield put(actions.requestMatchRuntimeBundleSuccess(matchRequest.data));
    } else {
        yield put(actions.requestMatchRuntimeBundleFailure(parseApiError(matchRequest)));
    }
}

export function* requestFetchCatalogTools() {
    const fetchRequest: ApiResponse = yield call(services.api.aiLab.fetchCatalogTools);
    if (fetchRequest.isSuccess) {
        yield put(actions.requestFetchCatalogToolsSuccess(fetchRequest.data));
    } else {
        const errorMessage = parseApiError(fetchRequest);
        yield put(actions.requestFetchCatalogToolsFailure(errorMessage));
    }    
}

export function* requestDeleteAiTool(action: ActionType<typeof actions.requestDeleteAiTool>) {
    const { toolId, toolName } = action.payload;
    const deleteRequest: ApiResponse = yield call(services.api.aiLab.deleteAiTool, toolId);
    if (deleteRequest.isSuccess) {
        yield put(userActions.setAppToast({message: `"${toolName}" was deleted`, type: AppToastTypes.Success}));
        yield put(actions.requestFetchCatalogTools());
    } else {
        const errorMessage = parseApiError(deleteRequest);
        yield put(userActions.setAppToast({message: `Failed to delete AI tool: ${errorMessage}`, type: AppToastTypes.Failure}));
    }
}

export function* requestAiToolConfiguration(action: ActionType<typeof actions.requestAiToolConfiguration>) {
    const { toolId } = action.payload;
    const fetchRequest: ApiResponse = yield call(services.api.aiLab.fetchAiToolConfiguration, toolId);
    if (fetchRequest.isSuccess) {
        yield put(actions.receiveAiToolConfiguration(fetchRequest.data));
    } else {
        const errorMessage = parseApiError(fetchRequest);
        yield put(userActions.setAppToast({message: `Failed to load AI tool configuration: ${errorMessage}`, type: AppToastTypes.Failure}));
    }
}

export function* updateAiToolConfiguration(action: ActionType<typeof actions.updateAiToolConfiguration>) {
    const { toolId, payload } = action.payload;
    const updateRequest: ApiResponse = yield call(services.api.aiLab.updateAiToolConfiguration, toolId, payload);
    if (updateRequest.isSuccess) {
        yield put(actions.updateAiToolConfigurationSuccess(updateRequest.data));
        yield put(actions.requestAiToolConfiguration(toolId));
    } else {
        const errorMessage = parseApiError(updateRequest);
        yield put(actions.updateAiToolConfigurationFailure(errorMessage));
        yield put(userActions.setAppToast({message: `Failed to update AI tool configuration: ${errorMessage}`, type: AppToastTypes.Failure}));
    }   
}

export function* createAiToolConfiguration(action: ActionType<typeof actions.createAiToolConfiguration>) {
    const { payload } = action.payload;
    const createRequest: ApiResponse = yield call(services.api.aiLab.createAiToolConfiguration, payload);
    if (createRequest.isSuccess) {
        const newToolId = createRequest.data.id;
        yield put(actions.createAiToolConfigurationSuccess(createRequest.data));
        yield put(actions.requestAiToolConfiguration(newToolId));
        yield put(userActions.setAppToast({message: "AI tool configuration created successfully", type: AppToastTypes.Success}));
    }
    else {
        const errorMessage = parseApiError(createRequest);
        yield put(actions.createAiToolConfigurationFailure(errorMessage));
        yield put(userActions.setAppToast({message: `Failed to create AI tool configuration: ${errorMessage}`, type: AppToastTypes.Failure}));
    }
}

export function* handleExecutionComplete(action: ActionType<typeof actions.setExecutionComplete>) {
    const { claimId } = action.payload;
    if (claimId && claimId > 0) {
        yield put(claimActions.requestCommentsActivityLog(claimId, claimsConstants.ACTIVITY_LOG_PAGING));
    }
}